import {
  Alert,
  Box,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {useState, useContext, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import AtplFooter from '../../components/ATPlab/atpFooter/AtplFooter';
import AtplLinearBar from '../../components/ATPlab/AtplLinearBar';
import {useClientApi} from '../../api/client';
import {CLIENT_TYPES} from '../../api/clientconfig';
import AuthContext from '../../context/AuthContext';
import {
  convertFeetAndInchesToCentimeters,
  convertPoundsToKilograms,
  convertKilogramsToPounds,
} from '../../utils/conversions';
import {VALIDATION_LIMITS} from '../../utils/miscellaneous';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import './atplGlobal.css';
// DateField and LocalizationProvider imports removed - using custom date inputs for large screens
import dayjs from 'dayjs';
import ScrollableDatePicker from '../../components/dateInput/scrollableDatePicker';
import {SimpleLoader} from '../../components/loading/simpleLoader';
import {useTheme} from '@mui/material/styles';
import AtplPrimaryBtnNew from '../../components/ATPlab/AtplPrimaryBtn/AtplPrimaryBtnNew';
import {ATPLBackButton} from '../../components/ATPlab/AtplBackBtn';

const toggleButtonStyles = {
  width: '32px',
  height: '27px',
  color: '#000000',
  fontSize: '12px',
  fontWeight: '500',
  '&.Mui-selected': {
    backgroundColor: '#1A1A1A4D',
    color: 'white',
  },
  '&.Mui-selected:hover': {
    backgroundColor: '#1A1A1A4D',
    color: '#000000',
  },
};

const measurementLabelStyle = {
  '& .MuiInputLabel-root': {
    fontSize: '19px',
    fontWeight: '500',
    textAlign: 'right',
    width: '100%',
    color: '#000000',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#000000',
    borderColor: '#000000',
  },
  '& .MuiInputBase-root.Mui-focused': {
    '& .MuiInput-underline': {
      borderBottom: '1px solid #000000 !important',
    },
    '&::after': {
      borderBottom: '1px solid #000000 !important',
    },
  },
};

const CommonErrorMessage = ({text}) => {
  return (
    <Typography
      variant="caption"
      color="error"
      display="flex"
      textAlign="start"
      gutterBottom
      sx={{
        fontSize: {
          xs: '12px',
          sm: '12px',
          md: '16px',
          lg: '16px',
        },
        padding: '10px 0px 0px 0px', // Padding for the text
        gutterBottom: true, // This adds bottom spacing
      }}
    >
      {text}
    </Typography>
  );
};

const calculateValidRanges = userData => {
  // Constants for BMI limits
  const MIN_BMI = 10;
  const MAX_BMI = 65;

  // Get current values in metric
  let heightInCm =
    userData.heightUnit === 'cm'
      ? Number(userData.height.cm)
      : convertFeetAndInchesToCentimeters(Number(userData.height.ft), Number(userData.height.in));

  // Calculate valid weight range for current height
  const heightInMeters = heightInCm / 100;
  let minWeight = MIN_BMI * (heightInMeters * heightInMeters);
  let maxWeight = MAX_BMI * (heightInMeters * heightInMeters);

  // Apply absolute limits in kg first
  minWeight = Math.max(minWeight, VALIDATION_LIMITS.WEIGHT.KG.MIN);
  maxWeight = Math.min(maxWeight, VALIDATION_LIMITS.WEIGHT.KG.MAX);

  return {
    weightRanges: {
      min: userData.weightUnit === 'kg' ? Math.round(minWeight) : Math.round(convertKilogramsToPounds(minWeight)),
      max: userData.weightUnit === 'kg' ? Math.round(maxWeight) : Math.round(convertKilogramsToPounds(maxWeight)),
      unit: userData.weightUnit,
    },
  };
};

export const AtplDemographics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {visitAsBack} = location.state || {};
  const clientApi = useClientApi(CLIENT_TYPES.ATP);
  const [currentStep, setCurrentStep] = useState(visitAsBack ? 2 : 0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const {setOrgUser} = useContext(AuthContext);
  const theme = useTheme();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const EXPECTED_ENCODED_KEY = process.env.REACT_APP_ATP_ENCODED_KEY_FOR_URL;
  // Authentication check on component load
  useEffect(() => {
    // Extract encoded client key from URL query params
    const params = new URLSearchParams(location.search);
    const encodedKey = params.get('key');

    // Check if encoded key in URL matches our expected encoded key
    // Also check if authenticated status is stored in localStorage
    const storedAuth = localStorage.getItem('atpl_authenticated');

    if (encodedKey === EXPECTED_ENCODED_KEY && storedAuth === 'true') {
      setIsAuthenticated(true);

      // Store authentication status if coming directly from URL with key
      if (encodedKey === EXPECTED_ENCODED_KEY) {
        localStorage.setItem('atpl_authenticated', 'true');
      }
    } else {
      // If not authenticated, redirect to landing page with key parameter
      return (
        <Box
          sx={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'white',
          }}
        >
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 500,
              textAlign: 'center',
              fontFamily: 'Poppins',
              padding: 3,
            }}
          >
            Authorization required. Please use a valid authentication link to access this page.
          </Typography>
        </Box>
      );
    }
  }, [location, navigate]);

  // const [selectedDate, setSelectedDate] = useState('');
  const [validation, setValidation] = useState({
    isHeightValidated: {isTrue: true, errorMsg: ''},
    isWeightValidated: {isTrue: true, errorMsg: ''},
    isBMIValidated: {isTrue: true, errorMsg: ''},
    isDateOfBirthValidate: {isTrue: true, errorMsg: ''},
    isDayValidated: {isTrue: true, errorMsg: ''},
    isMonthValidated: {isTrue: true, errorMsg: ''},
    isYearValidated: {isTrue: true, errorMsg: ''},
  });
  const defaultUserData = {
    gender: '', //since we sharing demographics to two two types of API one take string aother take genderID
    genderId: 1,
    age: '',
    weight: '',
    height: {cm: '', ft: '', in: ''},
    ethnicity: null,
    regularBp: '',
    isFrontendHeightImperialUnits: true,
    isFrontendWeightImperialUnits: true,
    heightUnit: 'in',
    weightUnit: 'lbs',
    // dateOfBirth: dayjs('2001-12-19T07:52:59.724Z')
    diabetes: null,
    isPregnant: null,
    isSmoke: null,
    isHighBP: null,
    hasHadHeartAttack: null,
    hasHadStroke: null,
    bpMeds: null,
    isHealthSymptoms: '',
    wellnessGoal: [],
    medicalCondition: [],
    allergies: [],
    isTakingSupplements: null,
    supplements: [],
    email: '',
    name: '',
    isPregnantId: '',
    dateOfBirth: null,
    dobDay: '',
    dobMonth: '',
    dobYear: '',
  };

  const [userData, setUserData] = useState(() => {
    const savedData = localStorage.getItem('userData');

    if (savedData) {
      const parsedData = JSON.parse(savedData);
      if (isLargeScreen) {
        // Ensure dateOfBirth is a Day.js object
        const dobDate = dayjs(parsedData.dateOfBirth);
        parsedData.dateOfBirth = dobDate;
        // Populate individual date fields for large screens
        if (dobDate.isValid()) {
          parsedData.dobDay = dobDate.format('DD');
          parsedData.dobMonth = dobDate.format('MM');
          parsedData.dobYear = dobDate.format('YYYY');
        }
      }
      return parsedData;
    }

    // Return defaultUserData if no data is found in localStorage
    return defaultUserData;
  });

  // will calculate and update age to send access API
  function calculateAge(birthDate) {
    const birth = new Date(birthDate); // Convert the input string to a Date object
    const today = new Date(); // Get the current date

    let age = today.getFullYear() - birth.getFullYear(); // Calculate the initial difference in years

    // Check if the birthday has already occurred this year
    const monthDiff = today.getMonth() - birth.getMonth();
    const dayDiff = today.getDate() - birth.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--; // If the birthday hasn't occurred yet this year, subtract 1 year
    }

    return age;
  }

  useEffect(() => {
    // Function to calculate the age
    const newAge = calculateAge(userData.dateOfBirth);
    setUserData(prev => ({
      ...prev,
      age: newAge,
    }));

    // Trigger form validation for dob
    const {isValid, validation} = validateForm();
    setValidation(validation);
  }, [userData.dateOfBirth]);

  //this will map gender string to its ID to send in demographics API
  useEffect(() => {
    let genderId = '';
    switch (userData.gender) {
      case 'male':
        genderId = 1;
        break;
      case 'female':
        genderId = 2;
        break;
      case 'Non-binary':
        genderId = 3;
        break;
      case 'Prefer not to say':
        genderId = 4;
        break;
      default:
        break;
    }
    setUserData(prevData => ({
      ...prevData,
      genderId: genderId,
    }));
  }, [userData.gender]);

  // Function to calculate BMI
  const calculateBMI = (heightCm, weightKg) => {
    const heightMeters = heightCm / 100;
    return weightKg / (heightMeters * heightMeters);
  };

  const validateForm = () => {
    const validateWeight = (weight, weightUnit) => {
      const numWeight = Number(weight);
      const limits = weightUnit === 'kg' ? VALIDATION_LIMITS.WEIGHT.KG : VALIDATION_LIMITS.WEIGHT.LBS;

      if (!weight) {
        return {
          isValid: false,
          errorMsg: 'Please enter your weight.',
        };
      }

      if (numWeight < limits.MIN || numWeight > limits.MAX) {
        return {
          isValid: false,
          errorMsg: `Weight must be between ${limits.MIN}${weightUnit} and ${limits.MAX}${weightUnit}.`,
        };
      }

      return {isValid: true, errorMsg: ''};
    };

    const validateHeight = (height, heightUnit) => {
      if (heightUnit === 'cm') {
        const numHeight = Number(height.cm);
        const limits = VALIDATION_LIMITS.HEIGHT.CM;

        if (!height.cm) {
          return {
            isValid: false,
            errorMsg: 'Please enter your height.',
          };
        }

        if (numHeight < limits.MIN || numHeight > limits.MAX) {
          return {
            isValid: false,
            errorMsg: `Height must be between ${limits.MIN}cm and ${limits.MAX}cm.`,
          };
        }
      } else {
        const ft = Number(height.ft);
        const inches = Number(height.in);
        const limits = VALIDATION_LIMITS.HEIGHT.FT_IN;

        if (!height.ft || !height.in) {
          return {
            isValid: false,
            errorMsg: 'Please enter both feet and inches.',
          };
        }

        // Convert height to total inches for comparison
        const totalInches = ft * 12 + inches;
        const minTotalInches = limits.MIN.FT * 12 + limits.MIN.IN;
        const maxTotalInches = limits.MAX.FT * 12 + limits.MAX.IN;

        if (totalInches < minTotalInches || totalInches > maxTotalInches) {
          return {
            isValid: false,
            errorMsg: `Height must be between ${limits.MIN.FT}'${limits.MIN.IN}" and ${limits.MAX.FT}'${limits.MAX.IN}".`,
          };
        }
      }

      return {isValid: true, errorMsg: ''};
    };

    let hasError = false;
    const updatedValidation = {...validation};

    //validate dob
    if (currentStep === 1) {
      if (isLargeScreen) {
        // For large screens, validate individual date fields
        const dateValidation = validateDateFields();
        Object.assign(updatedValidation, dateValidation.validation);
        if (!dateValidation.isValid) {
          hasError = true;
        }
      } else {
        // For mobile screens, validate dateOfBirth
        if (!userData.dateOfBirth) {
          updatedValidation.isDateOfBirthValidate = {
            isTrue: false,
            errorMsg: 'Please enter your date of birth.',
          };
          hasError = true;
        } else {
          const age = calculateAge(userData.dateOfBirth);
          if (age < 13 || age > 120) {
            updatedValidation.isDateOfBirthValidate = {
              isTrue: false,
              errorMsg: 'Age must be between 13 and 120 years.',
            };
            hasError = true;
          } else if (!dayjs(userData.dateOfBirth).isValid()) {
            updatedValidation.isDateOfBirthValidate = {isTrue: false, errorMsg: 'Please enter a valid date of birth.'};
            hasError = true;
          } else {
            updatedValidation.isDateOfBirthValidate = {isTrue: true, errorMsg: ''};
          }
        }
      }
    }

    //validate height & weight
    if (currentStep === 2) {
      // Height validation
      const heightValidation = validateHeight(userData.height, userData.heightUnit);
      updatedValidation.isHeightValidated = {
        isTrue: heightValidation.isValid,
        errorMsg: heightValidation.errorMsg,
      };
      if (!heightValidation.isValid) hasError = true;

      // Weight validation
      const weightValidation = validateWeight(userData.weight, userData.weightUnit);
      updatedValidation.isWeightValidated = {
        isTrue: weightValidation.isValid,
        errorMsg: weightValidation.errorMsg,
      };
      if (!weightValidation.isValid) hasError = true;

      // BMI validation (only if height and weight are valid)
      if (heightValidation.isValid && weightValidation.isValid) {
        const heightInCm =
          userData.heightUnit === 'cm'
            ? Number(userData.height.cm)
            : convertFeetAndInchesToCentimeters(Number(userData.height.ft), Number(userData.height.in));

        const weightInKg =
          userData.weightUnit === 'kg' ? Number(userData.weight) : convertPoundsToKilograms(Number(userData.weight));

        const bmi = calculateBMI(heightInCm, weightInKg);

        if (bmi < 10 || bmi > 65) {
          const {weightRanges} = calculateValidRanges(userData);
          let errorMessage = 'Height and weight combination results in an invalid BMI.';

          if (userData.heightUnit === 'cm') {
            errorMessage += ` For your height of ${heightInCm}cm, please adjust weight to between ${weightRanges.min}${weightRanges.unit} and ${weightRanges.max}${weightRanges.unit}.`;
          } else {
            const ft = userData.height.ft;
            const inches = userData.height.in;
            errorMessage += ` For your height of ${ft}'${inches}", please adjust weight to between ${weightRanges.min}${weightRanges.unit} and ${weightRanges.max}${weightRanges.unit}.`;
          }

          updatedValidation.isBMIValidated = {isTrue: false, errorMsg: errorMessage};
          hasError = true;
        } else {
          updatedValidation.isBMIValidated = {isTrue: true, errorMsg: ''};
        }
      }
    }

    return {isValid: !hasError, validation: updatedValidation};
  };

  // Helper function to create the demographics object
  const createDemographicsObject = (userData, finalHeight, finalWeight) => {
    return {
      age: parseInt(userData.age),
      birthDate: userData.dateOfBirth,
      gender: userData.genderId,
      height: finalHeight,
      weight: finalWeight,
      isFrontendHeightImperialUnits: userData.isFrontendHeightImperialUnits,
      isFrontendWeightImperialUnits: userData.isFrontendWeightImperialUnits,
      ethnicity: null,
      isSmoke: null,
      isDiabetes: null,
      hasHighBP: null,
      hasHadStroke: null,
      hasHadAttack: null,
      isOnBpMeds: null,
      isPregnant: null,
      hasMedicalCondition: null,
      medicalConditionNames: null,
      hasAllergies: null,
      allergyNames: null,
      hasSupplements: null,
      supplements: null,
      wellnessGoals: null,
    };
  };

  const handleSubmit = async () => {
    console.log(userData);

    try {
      // Validate form before submission
      const {isValid, validation: updatedValidation} = validateForm();
      setValidation(updatedValidation);

      if (!isValid) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      // Handle height conversion and rounding
      const finalHeight = Math.round(
        userData.isFrontendHeightImperialUnits
          ? convertFeetAndInchesToCentimeters(Number(userData.height.ft), Number(userData.height.in))
          : Number(userData.height.cm),
      );

      // Handle weight conversion and rounding
      const finalWeight = Math.round(
        userData.isFrontendWeightImperialUnits
          ? convertPoundsToKilograms(Number(userData.weight))
          : Number(userData.weight),
      );

      const demographics = {
        age: parseInt(userData.age),
        gender: userData.gender,
        height: finalHeight,
        weight: finalWeight,
      };

      //first Access API call
      const response = await clientApi.postDemographics(demographics);

      if (response && response.data) {
        let assesmentId = response.data.assessment_id;
        localStorage.setItem('atpl-assessment-id', response.data.assessment_id);
        localStorage.setItem('atpl-session-id', response.data.session_id);
        setOrgUser(response.data.session_id);
        localStorage.setItem('atpl-redirect-url', response.data.redirect_url);

        // Prepare the demographics object for second API call
        const demographics2 = createDemographicsObject(userData, finalHeight, finalWeight);

        // Second API call for demographics this require assessmentId which we are getting from first API csll
        try {
          const response2 = await clientApi.postDemographicsNew(demographics2, assesmentId);
          navigate('/atpl-pre-face-scan');
        } catch (error) {
          setError('Failed to submit demographics. Please try again.');
          setValidation(prev => ({
            ...prev,
            isSubmitError: {
              isTrue: false,
              errorMsg: 'Failed to submit demographics. Please try again.',
            },
          }));
          console.error('Failed to post demographics:', error);
        }
      } else {
        throw new Error('Invalid API response');
      }
    } catch (err) {
      console.error('Error submitting demographics:', err);
      setError('Failed to submit demographics. Please try again.');
      setValidation(prev => ({
        ...prev,
        isSubmitError: {
          isTrue: false,
          errorMsg: 'Failed to submit demographics. Please try again.',
        },
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const saveDataInLocalStorage = () => {
    // Check if userData exists in localStorage
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      // If userData exists, merge with new data
      const currentUserData = JSON.parse(storedUserData);
      const updatedUserData = {...currentUserData, ...userData};
      // Store merged data back to localStorage
      localStorage.setItem('userData', JSON.stringify(updatedUserData));
    } else {
      // If userData doesn't exist, store the current userData
      localStorage.setItem('userData', JSON.stringify(userData));
    }
  };

  // Next step logic
  const handleNextStep = async () => {
    const {isValid, validation: updatedValidation} = validateForm();
    // Set the validation state with the updated error messages
    setValidation(updatedValidation);
    if (currentStep === 3 && isValid) {
      handleSubmit();
      saveDataInLocalStorage();
    } else if (isValid) {
      saveDataInLocalStorage();
      setCurrentStep(prev => prev + 1); // Move to the next step
    }
  };

  const handleOptionSelect = (field, value) => {
    setUserData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Special handling for blood pressure in step 4
    if (field === 'bloodPressure' && currentStep === 3) {
      // Save regularBp to localStorage
      localStorage.setItem('regularBp', value);

      // Also update the userData in localStorage
      saveDataInLocalStorage();

      // Call handleSubmit to continue with form submission
      handleSubmit();
    } else {
      // For all other fields, continue with normal flow
      handleNextStep();
    }
  };

  const handleUnitChange = (event, newUnit, type) => {
    if (!newUnit) return;

    setUserData(prev => ({
      ...prev,
      ...(type === 'height'
        ? {
            heightUnit: newUnit,
            height: {ft: '', in: '', cm: ''},
            isFrontendHeightImperialUnits: newUnit === 'in',
          }
        : {
            weightUnit: newUnit,
            weight: '',
            isFrontendWeightImperialUnits: newUnit === 'lbs',
          }),
    }));

    //reset validation state when unit changes
    setValidation(prevValidation => ({
      ...prevValidation,
      isWeightValidated: {isTrue: true, errorMsg: ''},
      isHeightValidated: {isTrue: true, errorMsg: ''},
    }));
  };

  // Callback function that will be passed to the ScrollableDatePicker
  const handleDateChange = date => {
    console.log('Selected Date in Parent: ', date);
    setUserData(prev => ({
      ...prev,
      dateOfBirth: date,
    }));
  };

  // Handle individual date field changes for large screens
  const handleDateInputChange = (field, value) => {
    // Remove any non-numeric characters
    const numericValue = value.replace(/\D/g, '');

    // Apply length limits
    let limitedValue = numericValue;
    if (field === 'dobDay' || field === 'dobMonth') {
      limitedValue = numericValue.slice(0, 2);
    } else if (field === 'dobYear') {
      limitedValue = numericValue.slice(0, 4);
    }

    // Clear validation errors as user types
    setValidation(prev => ({
      ...prev,
      isDayValidated: {isTrue: true, errorMsg: ''},
      isMonthValidated: {isTrue: true, errorMsg: ''},
      isYearValidated: {isTrue: true, errorMsg: ''},
      isDateOfBirthValidate: {isTrue: true, errorMsg: ''},
    }));

    // Update user data and create date object if all fields are filled
    setUserData(prev => {
      const newData = {
        ...prev,
        [field]: limitedValue,
      };

      // Check if all date fields are filled
      if (newData.dobDay && newData.dobMonth && newData.dobYear) {
        const dateString = `${newData.dobYear}-${newData.dobMonth.padStart(2, '0')}-${newData.dobDay.padStart(2, '0')}`;
        const dateObj = dayjs(dateString);
        if (dateObj.isValid()) {
          newData.dateOfBirth = dateObj;
        }
      }

      return newData;
    });
  };

  // Validate individual date fields
  const validateDateFields = () => {
    const currentYear = new Date().getFullYear();
    const updatedValidation = {...validation};
    let hasError = false;

    // Validate day
    const day = parseInt(userData.dobDay);
    if (!userData.dobDay) {
      updatedValidation.isDayValidated = {isTrue: false, errorMsg: 'Day required'};
      hasError = true;
    } else if (day < 1 || day > 31) {
      updatedValidation.isDayValidated = {isTrue: false, errorMsg: 'Invalid day'};
      hasError = true;
    } else {
      updatedValidation.isDayValidated = {isTrue: true, errorMsg: ''};
    }

    // Validate month
    const month = parseInt(userData.dobMonth);
    if (!userData.dobMonth) {
      updatedValidation.isMonthValidated = {isTrue: false, errorMsg: 'Month required'};
      hasError = true;
    } else if (month < 1 || month > 12) {
      updatedValidation.isMonthValidated = {isTrue: false, errorMsg: 'Invalid month'};
      hasError = true;
    } else {
      updatedValidation.isMonthValidated = {isTrue: true, errorMsg: ''};
    }

    // Validate year
    const year = parseInt(userData.dobYear);
    if (!userData.dobYear) {
      updatedValidation.isYearValidated = {isTrue: false, errorMsg: 'Year required'};
      hasError = true;
    } else if (year < 1900 || year > currentYear) {
      updatedValidation.isYearValidated = {isTrue: false, errorMsg: `Year must be between 1900-${currentYear}`};
      hasError = true;
    } else {
      updatedValidation.isYearValidated = {isTrue: true, errorMsg: ''};
    }

    // Validate complete date if individual fields are valid
    if (!hasError) {
      const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const dateObj = dayjs(dateString);

      if (!dateObj.isValid()) {
        updatedValidation.isDateOfBirthValidate = {isTrue: false, errorMsg: 'Invalid date combination'};
        hasError = true;
      } else {
        // Check age
        const age = currentYear - year;
        const hasHadBirthday = dayjs().isAfter(dateObj.add(age, 'year'));
        const actualAge = hasHadBirthday ? age : age - 1;

        if (actualAge < 13 || actualAge > 120) {
          updatedValidation.isDateOfBirthValidate = {isTrue: false, errorMsg: 'Age must be between 13 and 120 years'};
          hasError = true;
        } else {
          updatedValidation.isDateOfBirthValidate = {isTrue: true, errorMsg: ''};
          // Update dateOfBirth if valid
          setUserData(prev => ({
            ...prev,
            dateOfBirth: dateObj,
          }));
        }
      }
    }

    return {isValid: !hasError, validation: updatedValidation};
  };

  const onBackArrowClickHandler = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      case 3:
        return renderStep4();
      default:
        return null;
    }
  };

  const renderStep1 = () => (
    <StepContainer title="Are you...">
      {[
        {label: 'Female', value: 'female'},
        {label: 'Male', value: 'male'},
        {label: 'Non-binary', value: 'Non-binary'},
        {label: 'Prefer not to say', value: 'Prefer not to say'},
      ].map((option, index) => (
        <Box
          key={index}
          onClick={() => handleOptionSelect('gender', option.value)}
          sx={{
            width: {
              xs: '280px',
              sm: '300px',
              md: '400px', // Increased width for laptop screens
              lg: '450px', // Even wider for larger screens
            },
            height: {
              xs: '44px',
              md: '54px',
            },
            backgroundColor: 'rgba(26, 26, 26, 0.08)',
            backgroundColor: userData.gender === option.value ? 'var(--atplGreyColor)' : 'rgba(26, 26, 26, 0.08)',

            // border: '1px solid #b8b9bc',
            // boxShadow: "rgba(0, 0, 0, 0.15) 0px 1px",
            marginBottom: '8px',
            borderRadius: '10px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            color: '#000',
            // ...(isSingleOptionSelected ? preSelectStyleForSlectedSinglevalue : {}),
            '&:hover': {
              // backgroundColor: '#fff1ed',
              // borderColor: '#ee5c36',
              transform: 'scale(1.02)',
              color: 'white',
              backgroundColor: 'var(--atplGreyColor)',
            },
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: '14px',
                sm: '16px',
                md: '18px', // Larger font for laptop screens
              },
              fontWeight: '400',
              // color: '#000'
            }}
          >
            {option.label}
          </Typography>
        </Box>
      ))}
    </StepContainer>
  );

  const renderStep2 = () => (
    <StepContainer title="What is your date of birth?" onClickNext={handleNextStep} btnTitle="Next">
      <Stack
        sx={{
          width: {
            xs: '300px',
            sm: '300px',
            md: '350px',
            lg: '400px',
          },
          marginY: '30px',
        }}
      >
        {isLargeScreen ? (
          <Box sx={{marginTop: '20px'}}>
            <Box sx={{display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'flex-start'}}>
              {/* Day Input */}
              <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <TextField
                  label="DD"
                  variant="outlined"
                  type="text"
                  value={userData.dobDay}
                  onChange={e => handleDateInputChange('dobDay', e.target.value)}
                  inputProps={{maxLength: 2}}
                  sx={{
                    width: '80px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': {
                        borderColor: '#000000',
                      },
                      '&:hover fieldset': {
                        borderColor: 'var(--atplGreyColor)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--atplGreyColor)',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                      '&.Mui-focused': {
                        color: 'var(--atplGreyColor)',
                      },
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: '500',
                    },
                  }}
                />
                {!validation.isDayValidated.isTrue && (
                  <CommonErrorMessage text={validation.isDayValidated.errorMsg} sx={{fontSize: '12px', mt: 1}} />
                )}
              </Box>

              {/* Month Input */}
              <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <TextField
                  label="MM"
                  variant="outlined"
                  type="text"
                  value={userData.dobMonth}
                  onChange={e => handleDateInputChange('dobMonth', e.target.value)}
                  inputProps={{maxLength: 2}}
                  sx={{
                    width: '80px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': {
                        borderColor: '#000000',
                      },
                      '&:hover fieldset': {
                        borderColor: 'var(--atplGreyColor)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--atplGreyColor)',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                      '&.Mui-focused': {
                        color: 'var(--atplGreyColor)',
                      },
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: '500',
                    },
                  }}
                />
                {!validation.isMonthValidated.isTrue && (
                  <CommonErrorMessage text={validation.isMonthValidated.errorMsg} sx={{fontSize: '12px', mt: 1}} />
                )}
              </Box>

              {/* Year Input */}
              <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <TextField
                  label="YYYY"
                  variant="outlined"
                  type="text"
                  value={userData.dobYear}
                  onChange={e => handleDateInputChange('dobYear', e.target.value)}
                  inputProps={{maxLength: 4}}
                  sx={{
                    width: '100px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': {
                        borderColor: '#000000',
                      },
                      '&:hover fieldset': {
                        borderColor: 'var(--atplGreyColor)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--atplGreyColor)',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                      '&.Mui-focused': {
                        color: 'var(--atplGreyColor)',
                      },
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: '500',
                    },
                  }}
                />
                {!validation.isYearValidated.isTrue && (
                  <CommonErrorMessage text={validation.isYearValidated.errorMsg} sx={{fontSize: '12px', mt: 1}} />
                )}
              </Box>
            </Box>

            {/* General date validation error */}
            {!validation.isDateOfBirthValidate.isTrue && (
              <Box sx={{mt: 2, textAlign: 'center'}}>
                <CommonErrorMessage text={validation.isDateOfBirthValidate.errorMsg} />
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{marginTop: '20px'}}>
            <ScrollableDatePicker onDateChange={handleDateChange} initialDate={userData.dateOfBirth} />
          </Box>
        )}

        {/* Mobile validation error */}
        {!isLargeScreen && (
          <Box sx={{marginTop: '50px'}}>
            <CommonErrorMessage
              text={!validation.isDateOfBirthValidate.isTrue && validation.isDateOfBirthValidate.errorMsg}
            />
          </Box>
        )}
      </Stack>
    </StepContainer>
  );

  const renderStep3 = () => (
    <StepContainer
      title="What is your height and weight?"
      onClickNext={handleNextStep}
      btnTitle="Next"
      StepContainerSx={{
        gap: '28px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: {
            xs: '290px',
            sm: '350px',
            md: '400px',
            lg: '380px',
          },
        }}
      >
        {/* Height Section */}
        <Box marginBottom="16px">
          <Stack display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{mb: 1, fontSize: '17px', fontWeight: '500'}}>Height</Typography>
            <ToggleButtonGroup
              value={userData.heightUnit}
              exclusive
              onChange={(event, newUnit) => handleUnitChange(event, newUnit, 'height')}
              sx={{mb: 2}}
            >
              <ToggleButton value="in" sx={toggleButtonStyles}>
                in
              </ToggleButton>
              <ToggleButton value="cm" sx={toggleButtonStyles}>
                cm
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {userData.heightUnit === 'in' ? (
            <Box sx={{display: 'flex', gap: 2}}>
              <TextField
                label="ft"
                variant="standard"
                type="number"
                value={userData.height.ft}
                sx={measurementLabelStyle}
                InputLabelProps={{shrink: false}}
                onChange={e =>
                  setUserData(prev => ({
                    ...prev,
                    height: {...prev.height, ft: e.target.value},
                  }))
                }
                fullWidth
              />

              <TextField
                label="in"
                variant="standard"
                type="number"
                sx={measurementLabelStyle}
                InputLabelProps={{
                  shrink: false, // Prevent the label from shrinking on focus
                }}
                value={userData.height.in}
                onChange={e =>
                  setUserData(prev => ({
                    ...prev,
                    height: {...prev.height, in: e.target.value},
                  }))
                }
                fullWidth
              />
            </Box>
          ) : (
            <TextField
              label="cm"
              variant="standard"
              type="number"
              fullWidth
              sx={measurementLabelStyle}
              InputLabelProps={{shrink: false}}
              value={userData.height.cm}
              onChange={e =>
                setUserData(prev => ({
                  ...prev,
                  height: {...prev.height, cm: e.target.value},
                }))
              }
            />
          )}
        </Box>

        {/* Weight Section */}
        <Box>
          <Stack display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{mb: 1, fontSize: '17px', fontWeight: '500'}}>
              Weight
            </Typography>
            <ToggleButtonGroup
              value={userData.weightUnit}
              exclusive
              onChange={(event, newUnit) => handleUnitChange(event, newUnit, 'weight')}
              sx={{mb: 2}}
            >
              <ToggleButton value="lbs" sx={toggleButtonStyles}>
                lbs
              </ToggleButton>
              <ToggleButton value="kg" sx={toggleButtonStyles}>
                kg
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <TextField
            label={userData.weightUnit === 'lbs' ? 'lbs' : 'kg'}
            variant="standard"
            type="number"
            fullWidth
            sx={measurementLabelStyle}
            InputLabelProps={{
              shrink: false, // Prevent the label from shrinking on focus
            }}
            value={userData.weight}
            onChange={e =>
              setUserData(prev => ({
                ...prev,
                weight: e.target.value,
              }))
            }
          />
        </Box>
        {
          <CommonErrorMessage
            text={
              (!validation.isHeightValidated.isTrue && validation.isHeightValidated.errorMsg) ||
              (!validation.isWeightValidated.isTrue && validation.isWeightValidated.errorMsg) ||
              (!validation.isBMIValidated.isTrue && validation.isBMIValidated.errorMsg)
            }
          />
        }
      </Box>
    </StepContainer>
  );

  const renderStep4 = () => {
    return (
      <StepContainer title="What is your regular blood pressure?" onClickNext={handleSubmit}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: {
              xs: '280px',
              sm: '300px',
              md: '400px',
              lg: '450px',
            },
            gap: 2,
          }}
        >
          {/* Blood pressure selection buttons */}
          <Box
            onClick={() => handleOptionSelect('bloodPressure', 'normal')}
            sx={{
              width: '100%',
              height: {
                xs: '55px',
                md: '70px',
              },
              backgroundColor: userData.bloodPressure === 'normal' ? 'var(--atplGreyColor)' : 'rgba(26, 26, 26, 0.08)',
              marginBottom: '8px',
              borderRadius: '10px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              color: '#000',
              '&:hover': {
                transform: 'scale(1.02)',
                color: 'white',
                backgroundColor: '#F15C30',
              },
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: '14px',
                  sm: '16px',
                  md: '18px',
                },
                fontWeight: '400',
                textAlign: 'center',
              }}
            >
              <Typography sx={{fontWeight: 'bold'}}>Normal</Typography>
              Less than 120 mmHg
            </Typography>
          </Box>

          <Box
            onClick={() => handleOptionSelect('bloodPressure', 'prehypertension')}
            sx={{
              width: '100%',
              height: {
                xs: '55px',
                md: '70px',
              },
              backgroundColor:
                userData.bloodPressure === 'prehypertension' ? 'var(--atplGreyColor)' : 'rgba(26, 26, 26, 0.08)',
              marginBottom: '8px',
              borderRadius: '10px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              color: '#000',
              '&:hover': {
                transform: 'scale(1.02)',
                color: 'white',
                backgroundColor: '#F15C30',
              },
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: '14px',
                  sm: '16px',
                  md: '18px',
                },
                fontWeight: '400',
                textAlign: 'center',
              }}
            >
              <Typography sx={{fontWeight: 'bold'}}>Prehypertension</Typography>
              120 - 139 mmHg
            </Typography>
          </Box>

          <Box
            onClick={() => handleOptionSelect('bloodPressure', 'hypertension')}
            sx={{
              width: '100%',
              height: {
                xs: '55px',
                md: '70px',
              },
              backgroundColor:
                userData.bloodPressure === 'hypertension' ? 'var(--atplGreyColor)' : 'rgba(26, 26, 26, 0.08)',
              marginBottom: '8px',
              borderRadius: '10px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              color: '#000',
              '&:hover': {
                transform: 'scale(1.02)',
                color: 'white',
                backgroundColor: '#F15C30',
              },
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: '14px',
                  sm: '16px',
                  md: '18px',
                },
                fontWeight: '400',
                textAlign: 'center',
              }}
            >
              <Typography sx={{fontWeight: 'bold'}}>Hypertension </Typography>
              More than 140 mmHg
            </Typography>
          </Box>
        </Box>
      </StepContainer>
    );
  };

  // If not authenticated, show loading or redirect
  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'white',
        }}
      >
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 500,
            textAlign: 'center',
            fontFamily: 'Poppins',
            padding: 3,
          }}
        >
          Authorization required. Please use a valid authentication link to access this page.
        </Typography>
      </Box>
    );
  }

  return isLoading ? (
    <SimpleLoader
      loadingText="Loading..."
      textColor="#F15C30"
      // secondsToLoad={60}
      // isAlmostComplete={responseReturned}
    />
  ) : (
    <Box
      className="atpl-default-css-config" // Ensures the default ATPL CSS is applied to this element
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: {
          xs: '88vh', // Adjust height for mobile devices to account for the footer, making it responsive
          sm: '98vh',
          md: '100vh',
          lg: '100vh',
        }, // Full viewport height
        bgcolor: 'white',
        overflowX: 'hidden',
      }}
    >
      {/* Progress Bar */}
      <Box
        sx={{
          px: {xs: 2, sm: 4, md: 30},
          pt: {xs: '6px', sm: 2, md: 2},
          mb: {xs: '2px', sm: 1, md: 1, lg: 2}, //distance between stepper & title
        }}
      >
        <Stack textAlign="start">{currentStep > 0 && <ATPLBackButton onClick={onBackArrowClickHandler} />}</Stack>
        <AtplLinearBar value={(currentStep + 1) * (100 / 17)} />
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          boxSizing: 'border-box',
          flex: 1, // Takes up remaining space
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          px: {xs: 1, sm: 4},
          // mb: 4 // Space before footer
          mb: {xs: '14px', sm: 3, md: 4, lg: currentStep == 10 ? 1 : 4}, // if wellness step mb is 8px
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            // pt: { xs: 2, sm: 3, md: 3, lg : "2px" }
          }}
        >
          {renderStep()}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          width: '100%',
          // pb: 3,
          pb: {xs: '0px', sm: 2, md: 1, lg: 2},
          bgcolor: 'white',
        }}
      >
        <AtplFooter footerTitle="This health assessment is powered by" />
      </Box>

      {/* Show network error */}
      <Snackbar
        open={error}
        autoHideDuration={5000}
        onClose={() => setError(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          variant="filled"
          severity="error"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textAlign: 'center',
            mb: 8,
            fontSize: {
              xs: 16,
              sm: 18,
            },
            px: {
              xs: 2,
              sm: 5,
            },
            borderRadius: 3,
          }}
        >
          An error occurred.Please try again later.
        </Alert>
      </Snackbar>
    </Box>
  );
};

const StepContainer = ({title, children, StepContainerSx, btnTitle, onClickNext}) => {
  return (
    <Stack
      sx={{
        width: '100%',
        maxWidth: {
          xs: '300px',
          sm: '390px',
          md: '400px',
          lg: '800px',
        },
        // marginTop: "60px",
        mx: 'auto',
        alignItems: 'center',
        justifyContent: 'flex-start',
        // justifyContent: 'cenetr',
        gap: 3,
        ...StepContainerSx,
      }}
    >
      <Typography
        sx={{
          fontSize: {xs: '20px', sm: '23px', md: '26px'},
          fontWeight: '700',
          lineHeight: {xs: '24px', sm: '27px', md: '32px'},
          textAlign: 'center',
          px: {xs: 1, sm: 4},
          width: '100%',
          paddingTop: {
            xs: '10px',
            sm: 1,
          },
        }}
      >
        {title}
      </Typography>

      <Stack>{children}</Stack>
      {btnTitle && (
        <Stack
          sx={{
            width: '100%',
            alignItems: 'center',
            mt: {xs: 1, sm: 3},
            // ...btnsx
          }}
        >
          {btnTitle && (
            <AtplPrimaryBtnNew
              sx={{
                maxWidth: '342px',
                width: '100%',
                marginTop: {xs: '4px', sm: '30px', md: '20px', lg: '20px'},
              }}
              onClick={onClickNext}
              text={btnTitle}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default AtplDemographics;
